/**
 * Touch Optimized Interaction Component
 * Provides enhanced touch interactions with proper feedback and accessibility
 */

import React, { ReactNode, useRef, useState, useEffect } from 'react';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { useResponsiveAnimation } from '../lib/animations/useResponsiveAnimation';
import { useAccessibility } from '../lib/animations/useAccessibility';

interface TouchOptimizedInteractionProps {
  children: ReactNode;
  onTap?: () => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onLongPress?: () => void;
  swipeThreshold?: number;
  longPressDelay?: number;
  hapticFeedback?: boolean;
  visualFeedback?: boolean;
  accessibilityLabel?: string;
  disabled?: boolean;
  className?: string;
}

interface TouchState {
  isPressed: boolean;
  isPanning: boolean;
  swipeDirection: 'left' | 'right' | 'up' | 'down' | null;
  longPressTriggered: boolean;
}

const TouchOptimizedInteraction: React.FC<TouchOptimizedInteractionProps> = ({
  children,
  onTap,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  onLongPress,
  swipeThreshold = 50,
  longPressDelay = 500,
  hapticFeedback = true,
  visualFeedback = true,
  accessibilityLabel,
  disabled = false,
  className = ''
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout>();
  
  const [touchState, setTouchState] = useState<TouchState>({
    isPressed: false,
    isPanning: false,
    swipeDirection: null,
    longPressTriggered: false
  });

  const { deviceCapabilities } = useResponsiveAnimation({});
  const { announce } = useAccessibility();

  // Motion values for smooth interactions
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);
  
  // Transform values for visual feedback
  const rotateX = useTransform(y, [-100, 100], [5, -5]);
  const rotateY = useTransform(x, [-100, 100], [-5, 5]);

  // Haptic feedback function
  const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
    if (!hapticFeedback || !deviceCapabilities.isTouchDevice) return;
    
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [30]
      };
      navigator.vibrate(patterns[type]);
    }
  };

  // Handle touch start
  const handleTouchStart = () => {
    if (disabled) return;
    
    setTouchState(prev => ({ ...prev, isPressed: true, longPressTriggered: false }));
    
    if (visualFeedback) {
      scale.set(0.95);
    }
    
    triggerHapticFeedback('light');
    
    // Start long press timer
    if (onLongPress) {
      longPressTimerRef.current = setTimeout(() => {
        setTouchState(prev => ({ ...prev, longPressTriggered: true }));
        onLongPress();
        triggerHapticFeedback('heavy');
        
        if (accessibilityLabel) {
          announce(`Long press activated: ${accessibilityLabel}`);
        }
      }, longPressDelay);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (disabled) return;
    
    setTouchState(prev => ({ 
      ...prev, 
      isPressed: false, 
      isPanning: false,
      swipeDirection: null 
    }));
    
    if (visualFeedback) {
      scale.set(1);
    }
    
    x.set(0);
    y.set(0);
    
    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  // Handle tap
  const handleTap = () => {
    if (disabled || touchState.longPressTriggered) return;
    
    onTap?.();
    triggerHapticFeedback('medium');
    
    if (accessibilityLabel) {
      announce(`Activated: ${accessibilityLabel}`);
    }
  };

  // Handle pan (drag/swipe)
  const handlePan = (event: any, info: PanInfo) => {
    if (disabled) return;
    
    setTouchState(prev => ({ ...prev, isPanning: true }));
    
    // Clear long press timer when panning starts
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
    
    // Update motion values
    x.set(info.offset.x);
    y.set(info.offset.y);
    
    // Determine swipe direction
    const absX = Math.abs(info.offset.x);
    const absY = Math.abs(info.offset.y);
    
    if (absX > swipeThreshold || absY > swipeThreshold) {
      let direction: 'left' | 'right' | 'up' | 'down' | null = null;
      
      if (absX > absY) {
        direction = info.offset.x > 0 ? 'right' : 'left';
      } else {
        direction = info.offset.y > 0 ? 'down' : 'up';
      }
      
      setTouchState(prev => ({ ...prev, swipeDirection: direction }));
    }
  };

  // Handle pan end (swipe completion)
  const handlePanEnd = (event: any, info: PanInfo) => {
    if (disabled) return;
    
    const absX = Math.abs(info.offset.x);
    const absY = Math.abs(info.offset.y);
    
    // Check if swipe threshold was met
    if (absX > swipeThreshold || absY > swipeThreshold) {
      if (absX > absY) {
        // Horizontal swipe
        if (info.offset.x > 0 && onSwipeRight) {
          onSwipeRight();
          triggerHapticFeedback('medium');
          announce('Swiped right');
        } else if (info.offset.x < 0 && onSwipeLeft) {
          onSwipeLeft();
          triggerHapticFeedback('medium');
          announce('Swiped left');
        }
      } else {
        // Vertical swipe
        if (info.offset.y > 0 && onSwipeDown) {
          onSwipeDown();
          triggerHapticFeedback('medium');
          announce('Swiped down');
        } else if (info.offset.y < 0 && onSwipeUp) {
          onSwipeUp();
          triggerHapticFeedback('medium');
          announce('Swiped up');
        }
      }
    }
    
    handleTouchEnd();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Base animation props
  const animationProps = {
    style: { x, y, scale, rotateX, rotateY },
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onTap: handleTap,
    onPan: handlePan,
    onPanEnd: handlePanEnd,
    whileTap: visualFeedback ? { scale: 0.95 } : undefined,
    transition: { type: 'spring', stiffness: 400, damping: 30 }
  };

  // Get responsive animations
  const { animations } = useResponsiveAnimation(animationProps, {
    touchOptimized: true,
    mobileAnimations: {
      // Enhanced touch feedback on mobile
      whileTap: { scale: 0.9 },
      transition: { type: 'spring', stiffness: 500, damping: 35 }
    }
  });

  return (
    <motion.div
      ref={elementRef}
      className={`touch-optimized-interaction ${className} ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      }`}
      {...animations}
      aria-label={accessibilityLabel}
      role={onTap ? 'button' : undefined}
      tabIndex={onTap && !disabled ? 0 : undefined}
    >
      {children}
      
      {/* Visual feedback indicators */}
      {visualFeedback && touchState.isPressed && (
        <motion.div
          className="absolute inset-0 bg-blue-500 opacity-10 rounded-lg pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          exit={{ opacity: 0 }}
        />
      )}
      
      {/* Swipe direction indicator */}
      {touchState.swipeDirection && (
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-500 pointer-events-none"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
        >
          {touchState.swipeDirection === 'left' && '←'}
          {touchState.swipeDirection === 'right' && '→'}
          {touchState.swipeDirection === 'up' && '↑'}
          {touchState.swipeDirection === 'down' && '↓'}
        </motion.div>
      )}
      
      {/* Long press indicator */}
      {onLongPress && touchState.isPressed && !touchState.longPressTriggered && (
        <motion.div
          className="absolute inset-0 border-2 border-blue-500 rounded-lg pointer-events-none"
          initial={{ scale: 1, opacity: 0 }}
          animate={{ scale: 1.05, opacity: 1 }}
          transition={{ duration: longPressDelay / 1000 }}
        />
      )}
    </motion.div>
  );
};

export default TouchOptimizedInteraction;